import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AxiosInstance from '../../helpers/AxiosInstance';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'application/octet-stream', 'image/heic', 'image/heif'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const InternationalPaymentBanner = ({ mode = 'DZD', prefillOrderDetails = '', referenceCode = '' }) => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    emailConfirm: '',
    orderDetails: '',
    receiptFile: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, orderDetails: prefillOrderDetails || '' }));
  }, [prefillOrderDetails, referenceCode]);

  const validateFile = (file) => {
    if (!file) {
      return 'Veuillez ajouter un reçu de virement.';
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'Fichier trop volumineux (max 5MB).';
    }

    const type = file.type || 'application/octet-stream';
    if (!ALLOWED_TYPES.includes(type)) {
      return 'Format de fichier non supporté (PDF/JPG/PNG/HEIC).';
    }

    return '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const fileError = validateFile(file);
    if (fileError) {
      setSubmitStatus({ success: false, message: fileError });
      setFormData({ ...formData, receiptFile: null });
      return;
    }

    setSubmitStatus(null);
    setFormData({ ...formData, receiptFile: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!formData.name || !formData.email || !formData.emailConfirm || !formData.orderDetails) {
        throw new Error('Tous les champs sont obligatoires.');
      }

      if (formData.email !== formData.emailConfirm) {
        throw new Error('Les adresses email ne correspondent pas.');
      }

      const fileError = validateFile(formData.receiptFile);
      if (fileError) {
        throw new Error(fileError);
      }

      const formDataToSend = new FormData();
      const hasReferenceInDetails = referenceCode && formData.orderDetails.includes(referenceCode);
      const orderDetailsToSend = referenceCode && !hasReferenceInDetails
        ? `${referenceCode}\n${formData.orderDetails}`
        : formData.orderDetails;
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('emailConfirm', formData.emailConfirm);
      formDataToSend.append('orderDetails', orderDetailsToSend);
      if (referenceCode) {
        formDataToSend.append('referenceCode', referenceCode);
      }
      if (formData.receiptFile) {
        formDataToSend.append('receipt', formData.receiptFile);
      }

      await AxiosInstance.post('/visitor/international-payment/submit-receipt', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitStatus({ success: true, message: 'Votre reçu a été soumis avec succès. Nous vérifierons et enverrons vos livres rapidement.' });
      setFormData({ name: '', email: '', emailConfirm: '', orderDetails: '', receiptFile: null });
      setTimeout(() => setShowForm(false), 3000);
    } catch (error) {
      setSubmitStatus({ 
        success: false, 
        message: error.response?.data?.message || 'Échec de l’envoi. Vérifiez les champs et le fichier, puis réessayez ou contactez digitization.opu@gmail.com' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="international-payment-banner mb-4" id="international-payment-form">
      <div className="card border-0 shadow-sm" style={{ backgroundColor: '#e8f3ec', borderLeft: '4px solid #2c5530' }}>
        <div className="card-body">
          <h5 className="card-title mb-3 d-flex align-items-center" style={{ color: '#2c5530' }}>
            {mode === 'EUR' && (
              <span
                className="badge me-2"
                style={{ backgroundColor: '#2c5530', color: 'white' }}
              >
                International (EUR)
              </span>
            )}
            <i className="fas fa-globe me-2"></i>
            {t('bookPurchase.internationalCustomers', { defaultValue: 'Pour les Clients en dehors de l\'Algérie' })}
          </h5>
          <p className="card-text mb-3" style={{ color: '#495057' }}>
            {t('bookPurchase.internationalPaymentInfo', { defaultValue: 'Vous pouvez acheter des livres via virement bancaire. Envoyez votre reçu pour recevoir vos livres PDF.' })}
          </p>
          {referenceCode && (
            <div className="alert py-2" style={{ backgroundColor: '#dfeee4', color: '#2c5530' }}>
              <i className="fas fa-hashtag me-1"></i>
              Référence à indiquer dans votre virement : <strong>{referenceCode}</strong>
            </div>
          )}
          <div className="row mb-3">
            <div className="col-md-6">
              <p className="mb-2">
                <strong style={{ color: '#2c5530' }}>{t('bookPurchase.bankAccount', { defaultValue: 'Compte Bancaire :' })}</strong>
                <span className="ms-2 font-monospace" style={{ color: '#495057' }}>00300060060004202044</span>
              </p>
            </div>
            <div className="col-md-6">
              <p className="mb-2">
                <strong style={{ color: '#2c5530' }}>{t('bookPurchase.sendReceiptTo', { defaultValue: 'Envoyer le reçu à :' })}</strong>
                <a href="mailto:digitization.opu@gmail.com" className="ms-2" style={{ color: '#2c5530' }}>digitization.opu@gmail.com</a>
              </p>
            </div>
          </div>
          <button
            className="btn btn-sm"
            onClick={() => setShowForm(!showForm)}
            style={{
              backgroundColor: showForm ? '#6c757d' : '#2c5530',
              borderColor: showForm ? '#6c757d' : '#2c5530',
              color: 'white'
            }}
          >
            <i className="fas fa-paper-plane me-2"></i>
            {showForm ? t('bookPurchase.hideForm', { defaultValue: 'Masquer le formulaire' }) : t('bookPurchase.submitReceipt', { defaultValue: 'Soumettre le reçu' })}
          </button>

        {showForm && (
          <div className="mt-3 pt-3 border-top">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small">{t('bookPurchase.yourName', { defaultValue: 'Votre nom' })}</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t('bookPurchase.yourEmail', { defaultValue: 'Votre email' })}</label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t('bookPurchase.confirmEmail', { defaultValue: 'Confirmer votre email' })}</label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    value={formData.emailConfirm}
                    onChange={(e) => setFormData({ ...formData, emailConfirm: e.target.value })}
                    required
                  />
                  {formData.emailConfirm && formData.email !== formData.emailConfirm && (
                    <small className="text-danger">Les adresses email ne correspondent pas.</small>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label small">{t('bookPurchase.orderDetails', { defaultValue: 'Détails de commande (livres achetés)' })}</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="3"
                    value={formData.orderDetails}
                    onChange={(e) => setFormData({ ...formData, orderDetails: e.target.value })}
                    placeholder={t('bookPurchase.orderDetailsPlaceholder', { defaultValue: 'Liste des livres et prix...' })}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">{t('bookPurchase.transferReceipt', { defaultValue: 'Reçu de virement (PDF/Image)' })}</label>
                  <input
                    type="file"
                    className="form-control form-control-sm"
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                    onChange={handleFileChange}
                    required
                  />
                  <small className="text-muted">
                    {t('bookPurchase.receiptNote', { defaultValue: 'Chargez votre reçu de virement (PDF/JPG/PNG/HEIC, max 5MB)' })}
                  </small>
                </div>
                {submitStatus && (
                  <div className={`col-12 alert ${submitStatus.success ? 'alert-success' : 'alert-danger'} py-2`}>
                    {submitStatus.message}
                  </div>
                )}
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-sm"
                    disabled={submitting}
                    style={{
                      backgroundColor: '#2c5530',
                      borderColor: '#2c5530',
                      color: 'white'
                    }}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t('bookPurchase.submitting', { defaultValue: 'Envoi en cours...' })}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        {t('bookPurchase.submitReceipt', { defaultValue: 'Soumettre le reçu' })}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default InternationalPaymentBanner;
